// "Zur Sammlung …" aus der Kontaktliste (ST-SEA-002 Plan F3, Schnitt 3a;
// ADR-0065; Bauvorgabe DESIGN 3a, Abschnitt 4) und der Dialog "Neue Sammlung"
// der Uebersicht, die sich das Namensfeld teilen.
//
// EIN SATZ OHNE WAHL fuer den Umfang, wie beim Export: mit Auswahl die
// ausgewaehlten Zeilen (bis 250, ein Aufruf), sonst alle Treffer der Suche als
// Auftrag im Hintergrund. Danach steht in der Statusregion die sichtbare
// Gesamtzahl der Sammlung -- nie, wie viele hinzukamen (ADR-0065 Ziffer 4).

import { useCallback, useEffect, useRef, useState, type ReactElement } from 'react';
import {
  Body1,
  Button,
  Caption1,
  Dialog,
  DialogActions,
  DialogBody,
  DialogContent,
  DialogSurface,
  DialogTitle,
  Field,
  Input,
  MessageBar,
  MessageBarBody,
  MessageBarTitle,
  ProgressBar,
  Select,
  Spinner,
  makeStyles,
  mergeClasses,
  tokens,
} from '@fluentui/react-components';

import { plainMessageFor, type PlainMessage } from '../access.ts';
import { keepsRequest, requestFor, TRIM_AND_NFC, type HeldRequest } from '../client-request.ts';
import {
  ADD_PROGRESS_LABEL,
  addMembersInBlocks,
  cancelledPartlySentence,
  addScopeSentence,
  addUncertainMessage,
  COLLECTION_GONE,
  collectionHref,
  COLLECTIONS_UNREACHABLE,
  CREATE_UNCERTAIN,
  createCollection,
  createdAfterCancelSentence,
  createdButNotFilledMessage,
  createFill,
  fetchAllCollections,
  fetchFill,
  FILL_POLL_MILLISECONDS,
  fillFailureMessage,
  fillRunningSentence,
  MAXIMUM_COLLECTION_NAME_LENGTH,
  MEMBER_BLOCK_SIZE,
  NAME_TAKEN,
  nameHint,
  nameErrorSentence,
  nameProblem,
  partlyAddedMessage,
  progressText,
  STATIC_HINT,
  totalSentence,
  type CollectionCommandOutcome,
  type CollectionSummary,
  FILL_TOO_MANY_CONTEXTS,
  fillTooManyContexts,
} from '../collections.ts';
import { useEigeneHausarten } from '../use-eigene-hausarten.ts';
import type { SearchFilter } from '../parties.ts';
import { AppLink } from '../shell/router.tsx';
import { Vorgang } from '../shell/zustand.tsx';
import { NebenKnopf } from '../shell/nebenknopf.tsx';

export interface Zugang {
  readonly apiBaseUrl: string;
  readonly accessToken: () => Promise<string | undefined>;
  readonly onReauthenticate: () => void;
}

const NEU = '__neu__';
const SCOPE_ID = 'zur-sammlung-umfang';

const useStyles = makeStyles({
  inhalt: { display: 'flex', flexDirection: 'column', gap: '0.75rem' },
  hinweis: { color: tokens.colorNeutralForeground2 },
  verborgen: { position: 'absolute', width: '1px', height: '1px', overflow: 'hidden', clip: 'rect(0 0 0 0)', whiteSpace: 'nowrap' },
  aktionen: { flexWrap: 'wrap' },
  status: {
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'center',
    gap: '0.25rem 0.5rem',
    overflowWrap: 'anywhere',
  },
  // In einer umbrechenden Knopfleiste steht das Ergebnis in eigener Zeile hinter allen Knoepfen.
  unten: { flexBasis: '100%', order: 1 },
});

/** LIAISON hat geantwortet, nur ohne einen Grund, den dieser Dialog in Worte fassen kann. */
const NICHT_ANGENOMMEN = plainMessageFor(undefined, { status: 422, answered: true, hasCorrelationId: false });

/** `lesefehler`: nichts ist geschrieben, die Leiste ist ein Fehler; sonst eine Warnung, weil offen sein kann, was gilt. */
export function Meldung(props: { readonly message: PlainMessage; readonly alert?: boolean; readonly lesefehler?: boolean }): ReactElement {
  return (
    <>
    <MessageBar intent={props.lesefehler === true ? 'error' : 'warning'} {...(props.alert === true ? { role: 'alert' } : {})}>
      <MessageBarBody>
        {props.message.headline.length > 0 ? <MessageBarTitle>{props.message.headline}</MessageBarTitle> : undefined}
        <div>{props.message.explanation}</div>
        <div>{props.message.nextStep}</div>
      </MessageBarBody>
    </MessageBar>
    {/* Hinter der Leiste: mit role=alert waere sie sonst eine Live-Region in einer Live-Region. */}
    {props.message.correlationId === undefined ? undefined : <Vorgang nummer={props.message.correlationId} eingebettet />}
    </>
  );
}

/** Das Namensfeld aller Dialoge: Pflicht, Hinweis TM-C10, Zaehler erst ab 100 Zeichen, Fehler am Feld. */
export function NamensFeld(props: {
  readonly id: string;
  readonly wert: string;
  readonly onWert: (wert: string) => void;
  readonly fehler: string | undefined;
}): ReactElement {
  const laenge = [...props.wert].length;
  return (
    <Field
      label="Name"
      required
      validationState={props.fehler === undefined ? 'none' : 'error'}
      validationMessage={props.fehler ?? null}
      hint={nameHint(laenge)}
    >
      {/* Die Grenze in Codepunkten wie die API, nicht per maxLength: das zaehlt
          UTF-16-Einheiten und liesse nur 60 Emoji zu. */}
      <Input
        id={props.id}
        value={props.wert}
        onChange={(_e, data) => {
          const zeichen = [...data.value];
          props.onWert(zeichen.length > MAXIMUM_COLLECTION_NAME_LENGTH ? zeichen.slice(0, MAXIMUM_COLLECTION_NAME_LENGTH).join('') : data.value);
        }}
      />
    </Field>
  );
}

type Anlage =
  | { readonly outcome: 'angelegt'; readonly collection: CollectionSummary }
  | { readonly outcome: 'feld'; readonly satz: string }
  | { readonly outcome: 'meldung'; readonly message: PlainMessage };

/** Legt eine Sammlung an und uebersetzt den Ausgang fuer das Namensfeld. */
async function anlegen(zugang: Zugang, token: string, name: string, anfrage: string): Promise<Anlage> {
  const result: CollectionCommandOutcome = await createCollection({ baseUrl: zugang.apiBaseUrl, accessToken: token, name, clientRequestId: anfrage });
  if (result.outcome === 'applied') {
    return result.collection === undefined ? { outcome: 'meldung', message: CREATE_UNCERTAIN } : { outcome: 'angelegt', collection: result.collection };
  }
  if (result.outcome === 'conflict' && result.message === NAME_TAKEN) return { outcome: 'feld', satz: `${NAME_TAKEN.explanation} ${NAME_TAKEN.nextStep}` };
  if (result.outcome === 'invalid') {
    const amNamen = result.errors.find((error) => error.fieldPath === 'name');
    if (amNamen !== undefined) return { outcome: 'feld', satz: nameErrorSentence(amNamen.reasonCode) };
  }
  return { outcome: 'meldung', message: 'message' in result ? result.message : NICHT_ANGENOMMEN };
}

/** "Neue Sammlung" aus der Uebersicht: Name, Anlegen, dann weiter zur Sammlung. */
export function NeueSammlungDialog(
  props: Zugang & {
    readonly onSchliessen: () => void;
    readonly onAngelegt: (collection: CollectionSummary) => void;
    /** Abgebrochen, und die Anlage kam danach doch an: keine Navigation, nur die Ansage der Seite. */
    readonly onNachAbbruchAngelegt: (collection: CollectionSummary) => void;
  },
): ReactElement {
  const own = useStyles();
  const [name, setName] = useState('');
  const [feldFehler, setFeldFehler] = useState<string | undefined>(undefined);
  const [fehler, setFehler] = useState<PlainMessage | undefined>(undefined);
  const [busy, setBusy] = useState(false);
  const gehalten = useRef<HeldRequest | undefined>(undefined);
  // Abbrechen bleibt jederzeit moeglich (keine Tastaturfalle, WCAG 2.1.2);
  // danach wird nichts fortgesetzt.
  const abgebrochen = useRef(false);
  const abbrechen = (): void => {
    abgebrochen.current = true;
    props.onSchliessen();
  };

  const senden = async (): Promise<void> => {
    if (busy) return;
    const problem = nameProblem(name);
    if (problem !== undefined) {
      setFeldFehler(problem);
      document.getElementById('neue-sammlung-name')?.focus();
      return;
    }
    setBusy(true);
    setFehler(undefined);
    try {
      const token = await props.accessToken();
      if (token === undefined) {
        props.onReauthenticate();
        return;
      }
      const anfrage = requestFor(gehalten.current, { name }, undefined, TRIM_AND_NFC);
      gehalten.current = anfrage;
      const ergebnis = await anlegen(props, token, name, anfrage.id);
      if (abgebrochen.current) {
        // Scheiterte die Anlage nach dem Abbrechen, gibt es nichts zu sagen.
        if (ergebnis.outcome === 'angelegt') props.onNachAbbruchAngelegt(ergebnis.collection);
        return;
      }
      if (ergebnis.outcome !== 'meldung' || !ergebnis.message.retryable) gehalten.current = undefined;
      if (ergebnis.outcome === 'angelegt') {
        props.onAngelegt(ergebnis.collection);
        return;
      }
      if (ergebnis.outcome === 'feld') {
        setFeldFehler(ergebnis.satz);
        document.getElementById('neue-sammlung-name')?.focus();
        return;
      }
      setFehler(ergebnis.message);
    } catch {
      // Abgebrochen: die Kennung bleibt, ein neuer Versuch legt nichts doppelt an.
      if (!abgebrochen.current) setFehler(CREATE_UNCERTAIN);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open onOpenChange={(_, data) => { if (!data.open) abbrechen(); }}>
      <DialogSurface>
        <form
          onSubmit={(event) => {
            event.preventDefault();
            void senden();
          }}
        >
          <DialogBody>
            <DialogTitle>Neue Sammlung</DialogTitle>
            <DialogContent className={own.inhalt}>
              <NamensFeld
                id="neue-sammlung-name"
                wert={name}
                onWert={(wert) => {
                  setName(wert);
                  setFeldFehler(undefined);
                }}
                fehler={feldFehler}
              />
              {fehler !== undefined ? <Meldung message={fehler} alert /> : undefined}
            </DialogContent>
            <DialogActions className={own.aktionen}>
              <NebenKnopf onClick={abbrechen}>
                Abbrechen
              </NebenKnopf>
              <Button type="submit" appearance="primary" disabledFocusable={busy}>
                {busy ? 'Wird angelegt …' : 'Anlegen'}
              </Button>
            </DialogActions>
          </DialogBody>
        </form>
      </DialogSurface>
    </Dialog>
  );
}

/**
 * Was hinzukommen soll, eingefroren beim Absenden. "Erneut versuchen" wiederholt
 * GENAU diesen Umfang, nicht die Auswahl von jetzt.
 */
interface Umfang {
  readonly markiert: readonly string[];
  readonly suche: { readonly term?: string | undefined; readonly filter?: SearchFilter | undefined };
}

type Ergebnis = { readonly name: string; readonly collectionId: string; readonly umfang: Umfang } & (
  | { readonly art: 'fertig'; readonly memberCount: number }
  | { readonly art: 'laeuft'; readonly fillId: string }
  | { readonly art: 'gescheitert'; readonly message: PlainMessage }
  | { readonly art: 'nach-abbruch' }
  | { readonly art: 'abgebrochen-teil' }
);

/**
 * Knopf "Zur Sammlung …", Dialog und die Statusregion "Status: Zur Sammlung".
 * `markiert` leer heisst: alle Treffer der Suche. Ohne Suche (leeres `suche`)
 * muss `markiert` gefuellt sein: dann kommen genau die ausgewaehlten hinein.
 */
export function ZurSammlungKnopf(
  props: Zugang & {
    readonly suche: { readonly term?: string | undefined; readonly filter?: SearchFilter | undefined };
    readonly markiert: readonly string[];
    /** Die Trefferzahl in Worten, aus derselben Quelle wie beim Export. */
    readonly zahl: string;
    /** Steht der Knopf mitten in einer Knopfleiste, kommt das Ergebnis darunter statt neben ihn. */
    readonly ergebnisUnten?: boolean;
    /** Ersetzt den Umfangssatz, etwa "Hinzugefügt wird dieser Kontakt." im Kontaktkopf. */
    readonly umfangSatz?: string;
    /**
     * Im Kontaktkopf (Bauvorgabe 3b, Abschnitt 1): kein eigener Knopf, geoeffnet
     * aus dem Menue "Weitere Aktionen"; den Satz sagt die Statusregion des Kopfs,
     * und den Fokus gibt der Kopf an seinen Ausloeser zurueck.
     */
    readonly extern?: {
      readonly offen: boolean;
      readonly onSchliessen: () => void;
      readonly onErgebnis: (satz: string, collectionId: string) => void;
      /**
       * Ein Lauf mit mindestens einem Schreibversuch ist zu Ende, auch nach
       * Abbrechen, Fehler oder unklarem Ausgang: was in Sammlungen steht, weiss
       * dann nur der Server. Ein Abbrechen ohne Schreiben meldet nichts.
       */
      readonly onGeschrieben?: () => void;
    };
  },
): ReactElement {
  const own = useStyles();
  const [offen, setOffen] = useState(false);
  const [liste, setListe] = useState<{ readonly status: 'laedt' | 'geladen' | 'fehler'; readonly sammlungen: readonly CollectionSummary[]; readonly fehler?: PlainMessage }>({ status: 'laedt', sammlungen: [] });
  const [ziel, setZiel] = useState<string>(NEU);
  const [name, setName] = useState('');
  const [feldFehler, setFeldFehler] = useState<string | undefined>(undefined);
  const [fehler, setFehler] = useState<PlainMessage | undefined>(undefined);
  const [busy, setBusy] = useState(false);
  const [ergebnis, setErgebnis] = useState<Ergebnis | undefined>(undefined);
  // Was die Statusregion sagt. Hinter dem modalen Dialog ist die Seite aria-hidden; ein Satz,
  // der dort entsteht, ist schon da, wenn die Region zurueckkehrt, und wird nicht angesagt.
  // Er folgt dem Ergebnis deshalb erst, wenn kein Vorfahr der Region mehr verborgen ist.
  const [angesagt, setAngesagt] = useState<Ergebnis | undefined>(undefined);
  const regionRef = useRef<HTMLDivElement | null>(null);
  /** Gesendete Kennungen waehrend der Bloecke; undefined, solange nichts in Bloecken laeuft. */
  const [fortschritt, setFortschritt] = useState<number | undefined>(undefined);
  // Die Region haengt erst, wenn der Dialog einmal offen war: im Ruhezustand
  // bleibt "Status: Kontakte" die einzige Region der Seite (wie "Status: Export"),
  // und vor dem ersten Ergebnis steht sie schon im DOM, damit es angesagt wird.
  const [benutzt, setBenutzt] = useState(false);
  const anlage = useRef<HeldRequest | undefined>(undefined);
  const auftrag = useRef<HeldRequest | undefined>(undefined);
  const zuFokussieren = useRef(false);

  const { apiBaseUrl, accessToken, onReauthenticate } = props;
  // Fuer "so viele Verlage …" nach der Art der eigenen Haeuser (M7).
  const eigeneArten = useEigeneHausarten(offen, apiBaseUrl, accessToken);

  const listeLaden = useCallback(async (): Promise<void> => {
    setListe((alt) => ({ status: 'laedt', sammlungen: alt.sammlungen }));
    try {
      const token = await accessToken();
      if (token === undefined) {
        onReauthenticate();
        return;
      }
      const result = await fetchAllCollections({ baseUrl: apiBaseUrl, accessToken: token });
      if (result.outcome === 'loaded') {
        setListe({ status: 'geladen', sammlungen: result.collections });
        // Die neueste ist vorausgewaehlt; ohne Sammlung bleibt "neu".
        setZiel((alt) => (alt !== NEU && result.collections.some((s) => s.collectionId === alt) ? alt : (result.collections[0]?.collectionId ?? NEU)));
      } else {
        setListe({ status: 'fehler', sammlungen: [], fehler: result.message });
      }
    } catch {
      setListe({ status: 'fehler', sammlungen: [], fehler: COLLECTIONS_UNREACHABLE });
    }
  }, [accessToken, apiBaseUrl, onReauthenticate]);

  // Der Dialog ist offen -- als Ref, weil ein spaeter Lauf schliessen() aus
  // einer alten Closure ruft. Der Fokus geht nur beim echten Uebergang von
  // offen zu zu zurueck, nie ein zweites Mal nach einem Abbruch.
  const offenRef = useRef(false);
  const oeffnen = (): void => {
    setFehler(undefined);
    setFeldFehler(undefined);
    setName('');
    setZiel(NEU);
    zuFokussieren.current = true;
    setBenutzt(true);
    offenRef.current = true;
    setOffen(true);
    void listeLaden();
  };

  // Fluent gibt den Fokus bei einem nur bedingt eingehaengten Dialog nicht
  // zurueck; der Knopf bekommt ihn ausdruecklich (WCAG 2.4.3).
  const knopf = useRef<HTMLButtonElement | null>(null);
  // Abbrechen waehrend einer Anlage: danach kein Hinzufuegen und kein Auftrag.
  const abgebrochen = useRef(false);
  const laeuft = useRef(false);
  const schliessen = (): void => {
    if (!offenRef.current) return;
    offenRef.current = false;
    if (laeuft.current) abgebrochen.current = true;
    // Den Fokus vor dem Aushaengen aus dem Dialog nehmen: sonst bleiben Fluents
    // Fokuswaechter im Body stehen (axe aria-hidden-focus).
    (document.activeElement as HTMLElement | null)?.blur();
    setOffen(false);
    if (props.extern !== undefined) props.extern.onSchliessen();
    else setTimeout(() => knopf.current?.focus(), 0);
  };

  // Von aussen geoeffnet: einmal je Wechsel auf offen. Die Funktion lebt in
  // einem Ref, damit der Effekt nur am Schalter haengt.
  const oeffnenRef = useRef(oeffnen);
  oeffnenRef.current = oeffnen;
  const externOffen = props.extern?.offen === true;
  useEffect(() => {
    if (externOffen) oeffnenRef.current();
  }, [externOffen]);

  // Im Kopf traegt dessen Statusregion den Satz; hier entsteht er nur.
  const onErgebnis = props.extern?.onErgebnis;
  useEffect(() => {
    if (onErgebnis === undefined || ergebnis === undefined) return;
    if (ergebnis.art === 'fertig') onErgebnis(totalSentence(ergebnis.name, ergebnis.memberCount), ergebnis.collectionId);
    else if (ergebnis.art === 'nach-abbruch') onErgebnis(createdAfterCancelSentence(ergebnis.name, 'kontakte'), ergebnis.collectionId);
    // Nur bei einem neuen Ergebnis, nicht bei jedem neuen Rueckruf des Kopfs.
  }, [ergebnis]);

  // Nach dem Laden steht der Fokus auf der Wahl (bzw. dem Namen ohne Sammlung),
  // nicht auf "Abbrechen", das Fluent waehrend des Ladens als Erstes faende.
  useEffect(() => {
    if (!offen || liste.status === 'laedt' || !zuFokussieren.current) return;
    zuFokussieren.current = false;
    if (liste.sammlungen.length > 0) document.getElementById('zur-sammlung-ziel')?.focus();
    else document.getElementById('zur-sammlung-name')?.focus();
  }, [offen, liste]);

  useEffect(() => {
    const region = regionRef.current;
    const verborgen = (): boolean => region?.closest('[aria-hidden="true"]') != null;
    if (region === null || !verborgen()) {
      setAngesagt(ergebnis);
      return undefined;
    }
    const beobachter = new MutationObserver(() => {
      if (verborgen()) return;
      beobachter.disconnect();
      setAngesagt(ergebnis);
    });
    beobachter.observe(document.body, { subtree: true, attributes: true, attributeFilter: ['aria-hidden'] });
    return () => beobachter.disconnect();
  }, [ergebnis]);

  // Die Abfrage des Auftrags im Takt des Exports; beim Verlassen endet sie still, der Server arbeitet weiter.
  // Seriell: die naechste Abfrage startet erst nach der Antwort der letzten, damit
  // sich bei langsamer Antwort keine Abfragen stapeln.
  useEffect(() => {
    if (ergebnis?.art !== 'laeuft') return undefined;
    const { collectionId, fillId, name: zielName, umfang } = ergebnis;
    let aktiv = true;
    let timer: ReturnType<typeof setTimeout> | undefined;
    const fragen = async (): Promise<void> => {
      try {
        const token = await accessToken();
        if (token === undefined || !aktiv) return;
        const result = await fetchFill({ baseUrl: apiBaseUrl, accessToken: token, collectionId, fillId });
        if (!aktiv) return;
        if (result.outcome === 'gone') {
          setErgebnis({ art: 'gescheitert', name: zielName, collectionId, umfang, message: { ...COLLECTION_GONE, explanation: 'Sie wurde beendet.' } });
          return;
        }
        if (result.outcome === 'loaded' && result.fill.status === 'succeeded') {
          setErgebnis({ art: 'fertig', name: zielName, collectionId, umfang, memberCount: result.fill.memberCount ?? 0 });
          return;
        }
        if (result.outcome === 'loaded' && result.fill.status === 'failed') {
          setErgebnis({ art: 'gescheitert', name: zielName, collectionId, umfang, message: fillFailureMessage(result.fill.failureReason) });
          return;
        }
        // Laeuft noch, oder die Abfrage war gestoert: das zaehlt nicht als Ende.
      } catch {
        // wie oben: die naechste Abfrage fragt wieder
      }
      if (aktiv) timer = setTimeout(() => void fragen(), FILL_POLL_MILLISECONDS);
    };
    timer = setTimeout(() => void fragen(), FILL_POLL_MILLISECONDS);
    return () => {
      aktiv = false;
      if (timer !== undefined) clearTimeout(timer);
    };
  }, [ergebnis, accessToken, apiBaseUrl]);

  /** Fuegt hinzu bzw. startet den Auftrag; bei Misserfolg der Satz dazu. */
  /**
   * Fuegt hinzu bzw. startet den Auftrag. Bei Misserfolg der Satz und ob schon
   * ein Teil drin ist -- dann sagt der Satz das selbst und wird nicht als
   * "angelegt, aber noch nicht drin" umhuellt.
   */
  const befuellen = async (token: string, sammlung: CollectionSummary, umfang: Umfang): Promise<{ readonly message: PlainMessage; readonly teilweise: boolean } | undefined> => {
    if (umfang.markiert.length > 0) {
      // In Bloecken zu 250 nacheinander; ueber einem Block mit Fortschritt im Dialog (Bauvorgabe 3c, Abschnitt 4).
      const bloecke = umfang.markiert.length > MEMBER_BLOCK_SIZE;
      if (bloecke) setFortschritt(0);
      const result = await addMembersInBlocks({
        baseUrl: apiBaseUrl,
        accessToken: token,
        collectionId: sammlung.collectionId,
        partyIds: umfang.markiert,
        abgebrochen: () => abgebrochen.current,
        fortschritt: (gesendet) => {
          if (bloecke) setFortschritt(gesendet);
        },
      });
      setFortschritt(undefined);
      const basis = { name: sammlung.collectionName, collectionId: sammlung.collectionId, umfang };
      if (result.outcome === 'fertig') {
        setErgebnis({ ...basis, art: 'fertig', memberCount: result.collection.memberCount });
        return undefined;
      }
      if (result.outcome === 'abgebrochen') {
        // Ohne Block drin gibt es nichts zu sagen; sonst den Stand ohne Zahl.
        if (result.teilweise) setErgebnis({ ...basis, art: 'abgebrochen-teil' });
        return undefined;
      }
      if (abgebrochen.current) {
        // Nach dem Abbrechen scheiterte der laufende Block: der Dialog ist zu, also der Stand in der Statusregion.
        if (result.teilweise) setErgebnis({ ...basis, art: 'abgebrochen-teil' });
        return undefined;
      }
      return { message: result.teilweise ? partlyAddedMessage(sammlung.collectionName) : result.message, teilweise: result.teilweise };
    }
    const anfrage = requestFor(auftrag.current, {
      collectionId: sammlung.collectionId,
      term: umfang.suche.term ?? '',
      filter: JSON.stringify(umfang.suche.filter ?? {}),
    });
    auftrag.current = anfrage;
    const result = await createFill({ baseUrl: apiBaseUrl, accessToken: token, collectionId: sammlung.collectionId, ...umfang.suche, clientRequestId: anfrage.id });
    if (!keepsRequest(result)) auftrag.current = undefined;
    if (result.outcome === 'applied' && result.fill !== undefined) {
      setErgebnis({ art: 'laeuft', name: sammlung.collectionName, collectionId: sammlung.collectionId, umfang, fillId: result.fill.fillId });
      return undefined;
    }
    return { message: 'message' in result ? result.message : result.outcome === 'invalid' ? NICHT_ANGENOMMEN : addUncertainMessage(), teilweise: false };
  };

  const hinzufuegen = async (): Promise<void> => {
    // Der Knopf bleibt waehrend des Sendens fokussierbar (Esc erreicht so den Dialog); ein zweites Absenden zaehlt nicht.
    if (laeuft.current) return;
    const umfang: Umfang = { markiert: [...props.markiert], suche: props.suche };
    const neu = ziel === NEU || liste.sammlungen.length === 0;
    if (neu) {
      const problem = nameProblem(name);
      if (problem !== undefined) {
        setFeldFehler(problem);
        document.getElementById('zur-sammlung-name')?.focus();
        return;
      }
    }
    // Ein alter Ergebnissatz gilt fuer diesen Versuch nicht mehr.
    setErgebnis(undefined);
    setBusy(true);
    // Der Abbruch-Merker gilt je Lauf und wird erst hier zurueckgesetzt, nicht
    // beim Oeffnen: sonst setzte ein erneutes Oeffnen einen noch laufenden,
    // abgebrochenen Lauf fort. Ein neuer Lauf startet erst, wenn der alte fertig ist.
    abgebrochen.current = false;
    laeuft.current = true;
    setFehler(undefined);
    let geschrieben = false;
    // Wo ein Abbruch traf: danach richtet sich, was offen ist.
    let stufe: 'anlage' | 'befuellen' | undefined;
    let angelegtName: string | undefined;
    try {
      const token = await accessToken();
      if (token === undefined) {
        onReauthenticate();
        return;
      }
      let sammlung = liste.sammlungen.find((s) => s.collectionId === ziel);
      geschrieben = neu || sammlung !== undefined;
      if (neu) {
        const gehalten = requestFor(anlage.current, { name }, undefined, TRIM_AND_NFC);
        anlage.current = gehalten;
        stufe = 'anlage';
        const ergebnisAnlage = await anlegen(props, token, name, gehalten.id);
        if (ergebnisAnlage.outcome !== 'meldung' || !ergebnisAnlage.message.retryable) anlage.current = undefined;
        if (abgebrochen.current) {
          // Nichts fortsetzen; kam die Anlage an, sagt die Seite es ehrlich. Scheiterte sie, gibt es nichts zu sagen.
          if (ergebnisAnlage.outcome === 'angelegt') {
            const spaet = ergebnisAnlage.collection;
            setErgebnis({ art: 'nach-abbruch', name: spaet.collectionName, collectionId: spaet.collectionId, umfang: { markiert: [], suche: {} } });
          }
          return;
        }
        if (ergebnisAnlage.outcome === 'feld') {
          setFeldFehler(ergebnisAnlage.satz);
          document.getElementById('zur-sammlung-name')?.focus();
          return;
        }
        if (ergebnisAnlage.outcome === 'meldung') {
          setFehler(ergebnisAnlage.message);
          return;
        }
        sammlung = ergebnisAnlage.collection;
        angelegtName = sammlung.collectionName;
        // Ab jetzt steht sie in der Wahl und ist gewaehlt -- auch, wenn das Hinzufuegen gleich scheitert.
        const angelegt = sammlung;
        setListe((alt) => ({ status: 'geladen', sammlungen: [angelegt, ...alt.sammlungen] }));
        setZiel(angelegt.collectionId);
        setName('');
      }
      if (sammlung === undefined) {
        setFehler(COLLECTION_GONE);
        return;
      }
      stufe = 'befuellen';
      const misslungen = await befuellen(token, sammlung, umfang);
      if (misslungen === undefined) {
        // Ein abgebrochener Lauf schliesst nichts mehr: sein Dialog ist schon zu,
        // und ein inzwischen neu geoeffneter gehoert ihm nicht.
        if (!abgebrochen.current) schliessen();
        return;
      }
      setFehler(neu && !misslungen.teilweise ? createdButNotFilledMessage(sammlung.collectionName, misslungen.message) : misslungen.message);
      if (misslungen.message === COLLECTION_GONE) void listeLaden();
    } catch {
      if (!abgebrochen.current) {
        setFehler(
          stufe === 'anlage'
            ? CREATE_UNCERTAIN
            : stufe === 'befuellen'
              ? addUncertainMessage(angelegtName)
              : plainMessageFor(undefined, { status: 0, answered: false, hasCorrelationId: false }),
        );
      }
    } finally {
      laeuft.current = false;
      setBusy(false);
      if (geschrieben) props.extern?.onGeschrieben?.();
    }
  };

  const nochmal = async (): Promise<void> => {
    if (ergebnis === undefined) return;
    const token = await accessToken();
    if (token === undefined) {
      onReauthenticate();
      return;
    }
    const sammlung: CollectionSummary = { collectionId: ergebnis.collectionId, collectionName: ergebnis.name, createdAt: '', lockVersion: 0 };
    setErgebnis(undefined);
    try {
      const misslungen = await befuellen(token, sammlung, ergebnis.umfang);
      if (misslungen !== undefined) setErgebnis({ art: 'gescheitert', name: ergebnis.name, collectionId: ergebnis.collectionId, umfang: ergebnis.umfang, message: misslungen.message });
    } catch {
      setErgebnis({ art: 'gescheitert', name: ergebnis.name, collectionId: ergebnis.collectionId, umfang: ergebnis.umfang, message: addUncertainMessage() });
    } finally {
      props.extern?.onGeschrieben?.();
    }
  };

  const ohneSammlung = liste.status !== 'laedt' && liste.sammlungen.length === 0;
  const nameSichtbar = ohneSammlung || (liste.status === 'geladen' && ziel === NEU);

  return (
    <>
      {props.extern === undefined ? (
        <Button ref={(knoten: HTMLButtonElement | null) => { knopf.current = knoten; }} appearance="secondary" onClick={oeffnen}>
          Zur Sammlung …
        </Button>
      ) : undefined}
      {/* Die Region traegt nur den Satz; Link und Knopf stehen daneben, damit
          die Ansage nicht die Bedienelemente mitliest. */}
      {benutzt && props.extern === undefined ? (
      <div data-zur-sammlung-ergebnis="" className={mergeClasses(own.status, props.ergebnisUnten === true && own.unten)}>
        <div role="status" aria-live="polite" aria-label="Status: Zur Sammlung" className={own.status} ref={regionRef}>
          {angesagt?.art === 'fertig' ? (
            <Body1>{totalSentence(angesagt.name, angesagt.memberCount)}</Body1>
          ) : angesagt?.art === 'laeuft' ? (
            <>
              <Spinner size="tiny" aria-hidden="true" />
              <Body1>{fillRunningSentence(angesagt.name)}</Body1>
            </>
          ) : angesagt?.art === 'abgebrochen-teil' ? (
            <Body1>{cancelledPartlySentence(angesagt.name)}</Body1>
          ) : angesagt?.art === 'nach-abbruch' ? (
            <Body1>{createdAfterCancelSentence(angesagt.name, 'kontakte')}</Body1>
          ) : angesagt?.art === 'gescheitert' ? (
            <Body1>
              <strong>{angesagt.message.headline}</strong> {angesagt.message.explanation} {angesagt.message.nextStep}
            </Body1>
          ) : undefined}
        </div>
        {/* Neben der Region, nicht in ihr: die Ansage bleibt der Satz. */}
        {ergebnis?.art === 'gescheitert' && ergebnis.message.correlationId !== undefined ? <Vorgang nummer={ergebnis.message.correlationId} eingebettet /> : undefined}
        {ergebnis?.art === 'fertig' || ergebnis?.art === 'nach-abbruch' ? <AppLink to={collectionHref(ergebnis.collectionId)}>Sammlung öffnen</AppLink> : undefined}
        {ergebnis?.art === 'gescheitert' && ergebnis.message.retryable ? (
          <Button appearance="secondary" onClick={() => void nochmal()}>
            Erneut versuchen
          </Button>
        ) : undefined}
      </div>
      ) : undefined}
      {offen ? (
        <Dialog open onOpenChange={(_, data) => { if (!data.open) schliessen(); }}>
          <DialogSurface aria-describedby={SCOPE_ID}>
            <form
              onSubmit={(event) => {
                event.preventDefault();
                void hinzufuegen();
              }}
            >
              <DialogBody>
                <DialogTitle>Zur Sammlung hinzufügen</DialogTitle>
                <DialogContent className={own.inhalt}>
                  <Body1 id={SCOPE_ID}>{props.umfangSatz ?? addScopeSentence(props.markiert.length, props.zahl)}</Body1>
                  {/* Einmal angesagt beim Start; den Zwischenstand traegt die Leiste, eine Ansage je Block waere Laerm. */}
                  <div role="status" aria-live="polite" aria-label="Status: Hinzufügen" className={own.verborgen}>
                    {fortschritt !== undefined ? `${ADD_PROGRESS_LABEL}.` : ''}
                  </div>
                  {fortschritt !== undefined ? (
                    <Field label={ADD_PROGRESS_LABEL} hint={progressText(fortschritt, props.markiert.length)}>
                      <ProgressBar
                        value={fortschritt}
                        max={props.markiert.length}
                        aria-valuetext={progressText(fortschritt, props.markiert.length)}
                      />
                    </Field>
                  ) : undefined}
                  {ohneSammlung && liste.status === 'geladen' ? <Body1>Du hast noch keine Sammlung. Gib ihr einen Namen.</Body1> : undefined}
                  {liste.status === 'fehler' && liste.fehler !== undefined ? (
                    <>
                      <Meldung message={liste.fehler} lesefehler />
                      <div>
                        <Button appearance="secondary" onClick={() => void listeLaden()}>
                          Erneut versuchen
                        </Button>
                      </div>
                    </>
                  ) : undefined}
                  {!ohneSammlung ? (
                    <Field label="Sammlung">
                      <Select
                        id="zur-sammlung-ziel"
                        value={liste.status === 'laedt' ? '' : ziel}
                        disabled={liste.status === 'laedt'}
                        onChange={(_e, data) => {
                          setZiel(data.value);
                          setFeldFehler(undefined);
                        }}
                      >
                        {liste.status === 'laedt' ? (
                          <option value="">Sammlungen werden geladen …</option>
                        ) : (
                          <>
                            {liste.sammlungen.map((sammlung) => (
                              <option key={sammlung.collectionId} value={sammlung.collectionId}>
                                {sammlung.collectionName}
                              </option>
                            ))}
                            <option value={NEU}>Neue Sammlung …</option>
                          </>
                        )}
                      </Select>
                    </Field>
                  ) : undefined}
                  {nameSichtbar ? (
                    <NamensFeld
                      id="zur-sammlung-name"
                      wert={name}
                      onWert={(wert) => {
                        setName(wert);
                        setFeldFehler(undefined);
                      }}
                      fehler={feldFehler}
                    />
                  ) : undefined}
                  {/* Der Hinweis spricht von der Suche; mit eigenem Umfangssatz (Kontaktkopf) gibt es keine. */}
                  {/* Der Hinweis erklaert den Unterschied zu einer Suche; ohne Suche (nur Auswahl) entfaellt er (DESIGN Z2). */}
                  {props.umfangSatz === undefined && (props.suche.term !== undefined || props.suche.filter !== undefined) ? (
                    <Caption1 className={own.hinweis}>{STATIC_HINT}</Caption1>
                  ) : undefined}
                  {fehler !== undefined && (fehler !== FILL_TOO_MANY_CONTEXTS || eigeneArten.geklaert) ? (
                    // Haengt die Meldung an den Arten, wartet sie, bis sie geklaert sind (hoechstens 1,5 s).
                    <Meldung message={fehler === FILL_TOO_MANY_CONTEXTS ? fillTooManyContexts(eigeneArten.arten) : fehler} alert />
                  ) : undefined}
                </DialogContent>
                <DialogActions className={own.aktionen}>
                  <NebenKnopf onClick={schliessen}>
                    Abbrechen
                  </NebenKnopf>
                  <Button type="submit" appearance="primary" disabledFocusable={busy || liste.status === 'laedt'}>
                    {busy ? 'Wird hinzugefügt …' : 'Hinzufügen'}
                  </Button>
                </DialogActions>
              </DialogBody>
            </form>
          </DialogSurface>
        </Dialog>
      ) : undefined}
    </>
  );
}
